import { useState } from 'react';
import type { EbookDocument, EbookWorkspace } from '@wx-weiqi/contracts';
import { CheckCircledIcon, CopyIcon, FileTextIcon, ReaderIcon } from '@radix-ui/react-icons';
import type { EbookClient } from '../services/ebooks';
import { isEbookQuestionReady } from '../services/ebook-editing';
import { EbookShelfCover } from './EbookShelfCover';

type Publication = Awaited<ReturnType<EbookClient['releases']>>[number];
export function EbookPublishPanel({
  client,
  document,
  data,
  classes,
  classIds,
  studentIds,
  rights,
  releases,
  busy,
  dirty,
  refreshPending,
  onClasses,
  onRights,
  onPublish,
  onRefresh,
  onArrange,
  onRead,
  onRecords,
  onCopy,
  onDownload,
}: {
  client: EbookClient;
  document: EbookDocument;
  data: EbookWorkspace;
  classes: Awaited<ReturnType<EbookClient['classes']>>;
  classIds: string[];
  studentIds: string[];
  rights: boolean;
  releases: Publication[];
  busy: boolean;
  dirty: boolean;
  refreshPending: boolean;
  onClasses: (ids: string[]) => void;
  onRights: (checked: boolean) => void;
  onPublish: () => void;
  onRefresh: () => void;
  onArrange: () => void;
  onRead: (id: string) => void;
  onRecords: () => void;
  onCopy: () => void;
  onDownload: (path: string, filename: string) => void;
}) {
  const [search, setSearch] = useState('');
  const latest = releases[0];
  const visibleClasses = classes.filter((c) =>
    c.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const incomplete = data.book.questionIds.filter(
    (id) => !data.questions.some((q) => q.id === id && isEbookQuestionReady(q)),
  ).length;
  return (
    <section className="ebook-publishing-workspace" aria-label="发布电子书">
      <div className="ebook-publishing-columns">
        <div className="ebook-publishing-form">
          <div className="ebook-task-heading">
            <div>
              <h2>把教材交给学生</h2>
              <p>只发布已编排的题目。保存草稿后，还需发布或更新电子书。</p>
            </div>
          </div>
          <div className="ebook-class-heading">
            <h3>选择班级</h3>
            <input
              type="search"
              aria-label="搜索班级名称"
              placeholder="搜索班级名称"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="ebook-publish-classes">
            {visibleClasses.map((c) => (
              <label
                key={c.classId}
                className={`ebook-check${classIds.includes(c.classId) ? ' is-selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={classIds.includes(c.classId)}
                  disabled={busy || refreshPending}
                  onChange={(e) =>
                    onClasses(
                      e.target.checked
                        ? [...classIds, c.classId]
                        : classIds.filter((id) => id !== c.classId),
                    )
                  }
                />
                <span>{c.name}</span>
              </label>
            ))}
            {!visibleClasses.length && (
              <p className="ebook-muted">
                {classes.length
                  ? '未找到匹配的班级。'
                  : '暂无可管理班级，请先在学生信息中建立或分配班级。'}
              </p>
            )}
          </div>
          {studentIds.length > 0 && (
            <p className="ebook-muted">
              同时沿用原有指定学生范围（{studentIds.length} 人），发布时仍需至少选择一个班级。
            </p>
          )}
          <label className="ebook-check ebook-publish-rights">
            <input
              type="checkbox"
              checked={rights}
              disabled={busy || refreshPending}
              onChange={(e) => onRights(e.target.checked)}
            />
            我确认拥有本教材的使用和传播权限
          </label>
          <section className="ebook-publish-changes">
            <h3>{latest ? '本次更新' : '本次发布'}</h3>
            <p>
              <FileTextIcon />
              {data.book.questionIds.length} 道已编排题目
              {latest ? `（上次 ${latest.questionIds.length} 题）` : ''}
            </p>
            <p>
              <ReaderIcon />
              使用当前封面、题目和章节名称
            </p>
            <p>
              <CheckCircledIcon />
              {latest ? '学生原入口与历史作答记录保留' : '授权班级的学生可从电子书架进入'}
            </p>
          </section>
          {incomplete > 0 && (
            <p className="ebook-error" role="alert">
              编排中有 {incomplete} 道题尚未就绪，请返回校对后发布。
            </p>
          )}
        </div>
        <aside className="ebook-publishing-preview">
          <div className="ebook-panel-heading">
            <h3>教材预览</h3>
            {latest && (
              <button disabled={busy} onClick={() => onRead(latest.id)}>
                查看已发布版本
              </button>
            )}
          </div>
          <EbookShelfCover
            client={client}
            title={data.book.title}
            path={
              data.book.coverImage
                ? `/documents/${document.id}/covers/${data.book.coverImage}`
                : null
            }
          />
          <p className="ebook-notice">
            {refreshPending
              ? '已发布成功 · 等待刷新状态'
              : latest
                ? '已发布 · 可更新电子书'
                : '尚未发布'}
          </p>
          <dl>
            <div>
              <dt>授权范围</dt>
              <dd>
                {classIds.length} 个班级
                {studentIds.length ? `及 ${studentIds.length} 名指定学生` : ''}
              </dd>
            </div>
            <div>
              <dt>教材名称</dt>
              <dd>{data.book.title}</dd>
            </div>
            <div>
              <dt>编排题数</dt>
              <dd>{data.book.questionIds.length} 题</dd>
            </div>
          </dl>
          {latest && (
            <details className="ebook-publish-resources">
              <summary>阅读入口与导出</summary>
              <div className="ebook-actions">
                <button onClick={onCopy}>
                  <CopyIcon />
                  复制授权链接
                </button>
                <button onClick={onRecords}>学习记录</button>
              </div>
              <p className="ebook-muted">离线文件包含答案，更新后需重新导出。</p>
              <div className="ebook-actions">
                <button
                  onClick={() =>
                    onDownload(`/releases/${latest.id}/export?format=html`, 'go-ebook.html')
                  }
                >
                  互动 HTML
                </button>
                <button
                  onClick={() =>
                    onDownload(
                      `/releases/${latest.id}/export?format=zip&answers=false`,
                      'questions.zip',
                    )
                  }
                >
                  题面 SGF
                </button>
                <button
                  onClick={() =>
                    onDownload(
                      `/releases/${latest.id}/export?format=zip&answers=true`,
                      'solutions.zip',
                    )
                  }
                >
                  答案 SGF
                </button>
              </div>
            </details>
          )}
          {releases.length > 1 && (
            <details className="ebook-publication-history">
              <summary>更新记录（{releases.length} 次）</summary>
              <ul>
                {releases.map((r) => (
                  <li key={r.id}>
                    {new Date(r.createdAt).toLocaleString()} · {r.questionIds.length} 题
                  </li>
                ))}
              </ul>
            </details>
          )}
        </aside>
      </div>
      <footer className="ebook-workspace-footer">
        <span>
          {dirty ? '有未保存修改 · 发布时一并保存' : '草稿已保存'}
          {latest && ' · 更新后学生可见'}
        </span>
        <button disabled={busy || refreshPending} onClick={onArrange}>
          返回编排
        </button>
        {refreshPending ? (
          <button className="primary" disabled={busy} onClick={onRefresh}>
            刷新发布状态
          </button>
        ) : (
          <button
            className="primary"
            disabled={
              busy || !rights || !classIds.length || !data.book.questionIds.length || incomplete > 0
            }
            onClick={onPublish}
          >
            {busy ? '正在发布…' : latest ? '更新电子书' : '发布电子书'}
          </button>
        )}
      </footer>
    </section>
  );
}
